import React, { lazy, Suspense, useEffect, useState } from 'react';
import { WidgetContextProvider, useWidget } from '@guest-widgets/core';
import { ConfigurationSettingsDto } from '@guest-widgets/shared/src/types/__mocks__/ConfigurationSettingsDto';

import { LanguageLocaleCode } from '../i18n/i18n';

interface CustomElementProps {
  'customer-code': string;
  locale: string;
  product: string;
  configuration: string;
  'enable-features'?: string[];
  'disable-features'?: string;
  'sub-id'?: string;
  hasShadowDom?: boolean;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'booking-widget': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & CustomElementProps,
        HTMLElement
      >;
    }
  }
}

interface BookingProps {
  productId: string;
  customerCode: string;
  locale: LanguageLocaleCode;
  subId?: string;
  productType?: string;
  configuration?: ConfigurationSettingsDto;
  enableFeatures?: string[];
  disableFeatures?: string[];
}

const SHADOW_DOM_FEATURE = 'shadow-dom';

const WIDGET_URL = '/booking/v1/booking-widget.min.js';

export const Booking = ({
  customerCode,
  locale,
  subId,
  configuration,
  productId,
  productType,
  enableFeatures,
  disableFeatures,
}: BookingProps) => {
  const { stylesContainer, containerElement, instanceId } = useWidget();
  const [bookingContainer, setBookingContainer] = useState<HTMLElement>();
  const shadowRoot = containerElement.getRootNode() as ShadowRoot;
  const bookingId = 'booking-widget-container-' + instanceId;
  const disableFeaturesWithShadowDom = [...(disableFeatures || []), SHADOW_DOM_FEATURE].join(' ');

  useEffect(() => {
    const widgetContainter = shadowRoot.getElementById(bookingId);
    if (!widgetContainter) return;
    setBookingContainer(widgetContainter);
  }, [shadowRoot.getElementById(bookingId)]);

  useEffect(() => {
    const loadBookingWidget = async () => {
      try {
        await import(/* @vite-ignore */ `${import.meta.env.VITE_BASE_URL}${WIDGET_URL}`);
      } catch (err) {
        console.error('Failed to load Booking Widget:', err);
      }
    };

    loadBookingWidget();
  }, []);

  return (
    <div id={bookingId} data-wid={instanceId}>
      {bookingContainer && (
        <WidgetContextProvider
          element={bookingContainer}
          containerElement={bookingContainer}
          viewportElement={bookingContainer}
          stylesContainer={stylesContainer}
          hasShadowDom={false}
        >
          <Suspense fallback={null}>
            <booking-widget
              customer-code={customerCode}
              locale={locale}
              product={productId}
              product-type={productType}
              configuration={JSON.stringify(mapConfigurationToWidget(configuration))}
              enable-features={enableFeatures}
              disable-features={disableFeaturesWithShadowDom}
              sub-id={subId}
            ></booking-widget>
          </Suspense>
        </WidgetContextProvider>
      )}
    </div>
  );
};

/** Set fixed font size for booking-widget because it considers the parent (button-widget) font-size when calculating its own */
const mapConfigurationToWidget = (configuration?: ConfigurationSettingsDto) => {
  if (!configuration) return;

  return {
    ...configuration,
    fontSize: configuration.fontSize ?? 1,
  };
};
